import React, { useState } from 'react';
import { Input, Button, Row, Col, Slider } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined, CalendarOutlined } from '@ant-design/icons';
import PersianDatePicker from '../PersianDatePicker';
import AppSelect, { Option } from '../common/AppSelect';
import PriorityStars from '../common/PriorityStars';

/**
 * StandardFilterPanel - کامپوننت استاندارد فیلتر برای صفحات HR
 */
const StandardFilterPanel = ({
  searchValue = '',
  onSearchChange,
  onSearch,
  searchPlaceholder = 'جستجو...',
  quickFilters = [],
  advancedFilters = [],
  onFilterChange,
  onResetFilters,
  totalCount = null,
  showAdvancedFilters = false,
  showTotalCount = false,
  hideSearch = false,
  compactFilterLayout = false,
}) => {
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [dateRangeDrafts, setDateRangeDrafts] = useState({});

  const handleFilterChange = (key, value) => {
    if (onFilterChange) {
      onFilterChange(key, value);
    }
  };

  const renderDateRangePicker = (key, value, placeholder, rest = {}) => {
    const draft = dateRangeDrafts[key];
    const pickerValue = draft ?? value;

    return (
      <PersianDatePicker
        range
        value={pickerValue}
        onChange={(val) => {
          const isEmpty =
            !val ||
            (Array.isArray(val) && val.filter((item) => item != null && item !== '').length === 0);
          if (isEmpty) {
            setDateRangeDrafts((prev) => ({ ...prev, [key]: null }));
            handleFilterChange(key, null);
            return;
          }
          if (Array.isArray(val)) {
            const hasStart = val[0] != null && val[0] !== '';
            const hasEnd = val[1] != null && val[1] !== '';
            if (hasStart && hasEnd) {
              setDateRangeDrafts((prev) => ({ ...prev, [key]: null }));
              handleFilterChange(key, val);
              return;
            }
            if (hasStart || hasEnd) {
              setDateRangeDrafts((prev) => ({ ...prev, [key]: val }));
            }
          }
        }}
        placeholder={placeholder || 'بازه تاریخ'}
        style={{ fontFamily: 'CustomFont', width: '100%' }}
        allowClear
        {...rest}
      />
    );
  };

  const renderQuickFilter = (filter) => {
    const { key, type, label, placeholder, value, options = [], loading = false, filterOption, showSearch, ...rest } = filter;

    if (type === 'select') {
      return (
        <AppSelect
          key={key}
          value={value || undefined}
          onChange={(val) => handleFilterChange(key, val || null)}
          placeholder={placeholder || label}
          allowClear
          showSearch={showSearch}
          filterOption={filterOption || ((input, option) =>
            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          )}
          loading={loading}
          style={{ width: '100%', fontFamily: 'CustomFont' }}
          {...rest}
        >
          {options.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </AppSelect>
      );
    }

    if (type === 'date') {
      return (
        <div key={key} className="flex items-center gap-2 min-w-0">
          {rest.showIcon && <CalendarOutlined className="text-gray-600 dark:text-gray-400 text-xs flex-shrink-0" />}
          <PersianDatePicker
            value={value}
            onChange={(val) => handleFilterChange(key, val)}
            placeholder={placeholder || label}
            style={{ fontFamily: 'CustomFont', width: '100%' }}
            {...rest}
          />
        </div>
      );
    }

    if (type === 'dateRange') {
      return (
        <div key={key} className="min-w-0">
          {renderDateRangePicker(key, value, placeholder || label, rest)}
        </div>
      );
    }

    if (type === 'input') {
      return (
        <Input
          key={key}
          value={value || ''}
          onChange={(e) => handleFilterChange(key, e.target.value)}
          placeholder={placeholder || label}
          allowClear
          style={{ fontFamily: 'CustomFont' }}
          {...rest}
        />
      );
    }

    if (type === 'datePreset') {
      return (
        <AppSelect
          key={key}
          value={undefined}
          onChange={(val) => {
            if (!val) return;
            const preset = options.find((item) => item.value === val);
            if (preset?.range && typeof rest.onPreset === 'function') {
              rest.onPreset(preset.range);
            }
          }}
          placeholder={placeholder || label}
          allowClear
          style={{ width: '100%', fontFamily: 'CustomFont' }}
        >
          {options.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </AppSelect>
      );
    }

    if (type === 'priorityStars') {
      return (
        <div key={key} className="flex items-center gap-2 h-8 px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
          <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </span>
          <PriorityStars value={value} onChange={(next) => handleFilterChange(key, next)} size={18} />
          {value ? (
            <button type="button" className="text-xs text-gray-400 hover:text-gray-600" onClick={() => handleFilterChange(key, null)}>
              پاک
            </button>
          ) : null}
        </div>
      );
    }

    if (type === 'successRate') {
      return (
        <div key={key} className="px-3 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'CustomFont' }}>{label}</div>
          <Slider
            min={0}
            max={100}
            step={25}
            marks={{ 0: '0', 25: '25', 50: '50', 75: '75', 100: '100' }}
            value={value == null || value === '' ? 0 : Number(value)}
            onChange={(next) => handleFilterChange(key, next)}
            style={{ minWidth: 180, margin: '0 6px 6px' }}
            {...rest}
          />
        </div>
      );
    }

    return null;
  };

  const renderAdvancedFilter = (filter) => {
    const { key, type, label, placeholder, value, options = [], loading = false, ...rest } = filter;

    if (type === 'select') {
      return (
        <Col key={key} xs={24} sm={12} md={8} lg={6}>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </label>
          <AppSelect
            value={value || undefined}
            onChange={(val) => handleFilterChange(key, val || null)}
            placeholder={placeholder || 'همه'}
            allowClear
            showSearch={rest.showSearch}
            filterOption={rest.filterOption}
            loading={loading}
            style={{ width: '100%', fontFamily: 'CustomFont' }}
            {...rest}
          >
            {options.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </AppSelect>
        </Col>
      );
    }

    if (type === 'input') {
      return (
        <Col key={key} xs={24} sm={12} md={8} lg={6}>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </label>
          <Input
            value={value || ''}
            onChange={(e) => handleFilterChange(key, e.target.value)}
            placeholder={placeholder}
            allowClear
            style={{ fontFamily: 'CustomFont' }}
            {...rest}
          />
        </Col>
      );
    }

    if (type === 'date') {
      return (
        <Col key={key} xs={24} sm={12} md={8} lg={6}>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </label>
          <PersianDatePicker
            value={value}
            onChange={(val) => handleFilterChange(key, val)}
            placeholder={placeholder}
            style={{ fontFamily: 'CustomFont', width: '100%' }}
            {...rest}
          />
        </Col>
      );
    }

    if (type === 'priorityStars') {
      return (
        <Col key={key} xs={24} sm={12} md={8} lg={6}>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </label>
          <div className="flex items-center gap-2 h-8 px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
            <PriorityStars value={value} onChange={(next) => handleFilterChange(key, next)} size={18} />
            {value ? (
              <button type="button" className="text-xs text-gray-400 hover:text-gray-600" onClick={() => handleFilterChange(key, null)}>
                پاک
              </button>
            ) : null}
          </div>
        </Col>
      );
    }

    if (type === 'successRate') {
      return (
        <Col key={key} xs={24} sm={12} md={8} lg={6}>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </label>
          <Slider
            min={0}
            max={100}
            step={25}
            marks={{ 0: '0', 25: '25', 50: '50', 75: '75', 100: '100' }}
            value={value == null || value === '' ? 0 : Number(value)}
            onChange={(next) => handleFilterChange(key, next)}
            style={{ margin: '0 6px 6px' }}
            {...rest}
          />
        </Col>
      );
    }

    if (type === 'dateRange') {
      return (
        <Col key={key} xs={24} sm={12} md={8} lg={6}>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
            {label}
          </label>
          {renderDateRangePicker(key, value, placeholder, rest)}
          </Col>
      );
    }

    return null;
  };

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="mb-4">
        {compactFilterLayout ? (
          <>
            {!hideSearch && (
              <div className="mb-3">
                <Input
                  placeholder={searchPlaceholder}
                  allowClear
                  value={searchValue}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  prefix={<SearchOutlined />}
                  style={{ fontFamily: 'CustomFont', maxWidth: 480 }}
                  className="w-full sm:max-w-md"
                />
              </div>
            )}
            {quickFilters.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {quickFilters.map((filter) => renderQuickFilter(filter))}
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {!hideSearch && (
              <Input
                placeholder={searchPlaceholder}
                allowClear
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ fontFamily: 'CustomFont' }}
              />
            )}
            {quickFilters.map((filter) => renderQuickFilter(filter))}
          </div>
        )}
      </div>

      {showAdvancedFilters && advancedFilters.length > 0 && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setAdvancedExpanded(!advancedExpanded)}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FilterOutlined className="text-gray-600 dark:text-gray-400" />
              <span
                className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'CustomFont' }}
              >
                فیلترهای بیشتر
              </span>
            </div>
            {advancedExpanded ? (
              <span className="text-gray-400 dark:text-gray-500">▲</span>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">▼</span>
            )}
          </button>

          {advancedExpanded && (
            <div className="mt-4 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
              <Row gutter={[16, 16]}>
                {advancedFilters.map((filter) => renderAdvancedFilter(filter))}
              </Row>
              <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-gray-100 dark:border-gray-700">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={onResetFilters}
                  className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ریست فیلترها
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StandardFilterPanel;
